/**
 * GenerateTokenModal Component
 *
 * Modal for generating a new agent token.
 * CRITICAL: Token is only shown ONCE - user must copy it before closing.
 */

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { useGenerateAgentToken } from '../hooks/useGenerateAgentToken';
import { useRevokeAgentToken } from '../hooks/useRevokeAgentToken';
import type { DataAgent, DataAgentGenerateTokenDTO, DataAgentTokenResponse } from '../../../types/data-agent.types';
import { ClipboardDocumentIcon, CheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface GenerateTokenModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  dataAgent: DataAgent;
}

type ViewState = 'confirm' | 'generated' | 'revoke';

export const GenerateTokenModal: React.FC<GenerateTokenModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  dataAgent,
}) => {
  const [viewState, setViewState] = useState<ViewState>('confirm');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [tokenResponse, setTokenResponse] = useState<DataAgentTokenResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  const hasExistingToken = dataAgent.tokenGeneratedAt && !dataAgent.tokenRevokedAt;
  const isTokenExpired = dataAgent.tokenExpiresAt && dataAgent.tokenExpiresAt * 1000 < Date.now();

  const { register, handleSubmit, watch } = useForm<DataAgentGenerateTokenDTO>({
    defaultValues: {
      expiresInDays: 180,
    },
  });

  const expiresInDays = watch('expiresInDays');

  const { mutate: generateToken, isPending: isGenerating } = useGenerateAgentToken(
    tenantId,
    dataAgent.dataAgentId,
    {
      onSuccess: (response) => {
        setTokenResponse(response);
        setViewState('generated');
        setErrorMessage('');
      },
      onError: (error) => {
        setErrorMessage(error);
      },
    }
  );

  const { mutate: revokeToken, isPending: isRevoking } = useRevokeAgentToken(
    tenantId,
    dataAgent.dataAgentId,
    {
      onSuccess: () => {
        handleClose();
      },
      onError: (error) => {
        setErrorMessage(error);
      },
    }
  );

  const onSubmit = (data: DataAgentGenerateTokenDTO) => {
    setErrorMessage('');
    generateToken(data);
  };

  const handleCopyToken = async () => {
    if (tokenResponse?.token) {
      await navigator.clipboard.writeText(tokenResponse.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    if (viewState === 'generated' && !acknowledged) {
      return; // Prevent closing without acknowledgment
    }
    setViewState('confirm');
    setTokenResponse(null);
    setErrorMessage('');
    setCopied(false);
    setAcknowledged(false);
    onClose();
  };

  const handleRevoke = () => {
    setErrorMessage('');
    revokeToken();
  };

  const expirationOptions = [
    { value: 30, label: '30 days' },
    { value: 60, label: '60 days' },
    { value: 90, label: '90 days' },
    { value: 180, label: '180 days (recommended)' },
    { value: 365, label: '365 days' },
  ];

  const formatExpirationDate = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date.toLocaleDateString();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <Modal.Header>
        {viewState === 'generated' ? 'Token Generated' : 'Manage Agent Token'}
      </Modal.Header>
      <Modal.Body>
        {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

        {/* Confirm/Generate View */}
        {viewState === 'confirm' && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Agent Info */}
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm text-gray-600">
                Agent:{' '}
                <span className="font-medium">{dataAgent.dataAgentName}</span>
                <span className="ml-2 font-mono text-xs text-gray-500">
                  ({dataAgent.dataAgentCode})
                </span>
              </p>
            </div>

            {/* Existing Token Warning */}
            {hasExistingToken && !isTokenExpired && (
              <Alert variant="warning">
                <div className="flex items-start gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Existing Token Will Be Replaced</p>
                    <p className="text-sm mt-1">
                      This agent has an active token. Generating a new token will immediately
                      invalidate the existing one. The agent will need to be reconfigured with
                      the new token.
                    </p>
                  </div>
                </div>
              </Alert>
            )}

            {/* Token Expiration */}
            <FormField label="Token Expiration" required>
              <Select {...register('expiresInDays', { valueAsNumber: true })}>
                {expirationOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Token will expire on {formatExpirationDate(Number(expiresInDays))}
              </p>
            </FormField>

            {/* Actions */}
            <div className="flex items-center justify-between pt-4">
              <div>
                {hasExistingToken && !isTokenExpired && (
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => setViewState('revoke')}
                    disabled={isGenerating}
                  >
                    Revoke Token
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Button type="button" variant="secondary" onClick={handleClose} disabled={isGenerating}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" loading={isGenerating}>
                  Generate Token
                </Button>
              </div>
            </div>
          </form>
        )}

        {/* Token Generated View */}
        {viewState === 'generated' && tokenResponse && (
          <div className="space-y-4">
            <Alert variant="warning">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Save This Token Now</p>
                  <p className="text-sm mt-1">
                    This token will only be displayed once. You will not be able to view it
                    again after closing this dialog.
                  </p>
                </div>
              </div>
            </Alert>

            {/* Token Display */}
            <div className="relative">
              <div className="bg-gray-900 text-green-400 font-mono text-sm p-4 rounded-md break-all">
                {tokenResponse.token}
              </div>
              <Button
                variant="secondary"
                size="sm"
                className="absolute top-2 right-2"
                onClick={handleCopyToken}
              >
                {copied ? (
                  <>
                    <CheckIcon className="h-4 w-4 mr-1" />
                    Copied
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                    Copy
                  </>
                )}
              </Button>
            </div>

            {/* Token Info */}
            <div className="text-sm text-gray-600 space-y-1">
              <p>
                <span className="font-medium">Agent Code:</span> {tokenResponse.dataAgentCode}
              </p>
              <p>
                <span className="font-medium">Generated:</span>{' '}
                {new Date(tokenResponse.generatedAt).toLocaleString()}
              </p>
              <p>
                <span className="font-medium">Expires:</span>{' '}
                {new Date(tokenResponse.expiresAt).toLocaleString()}
              </p>
            </div>

            {/* Acknowledgment */}
            <div className="border-t pt-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  className="mt-1"
                />
                <span className="text-sm text-gray-700">
                  I have copied and securely stored this token. I understand that I will not be
                  able to view it again.
                </span>
              </label>
            </div>

            {/* Close Button */}
            <div className="flex justify-end pt-2">
              <Button variant="primary" onClick={handleClose} disabled={!acknowledged}>
                Done
              </Button>
            </div>
          </div>
        )}

        {/* Revoke Confirmation View */}
        {viewState === 'revoke' && (
          <div className="space-y-4">
            <Alert variant="error">
              <div className="flex items-start gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Revoke Token?</p>
                  <p className="text-sm mt-1">
                    This will immediately invalidate the current token. The agent will no longer
                    be able to authenticate until a new token is generated and configured.
                  </p>
                </div>
              </div>
            </Alert>

            <div className="flex items-center justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setViewState('confirm')}
                disabled={isRevoking}
              >
                Cancel
              </Button>
              <Button variant="danger" onClick={handleRevoke} loading={isRevoking}>
                Revoke Token
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};
